import { DefaultRootState } from 'react-redux';

import { BannerImageCropType, ImageComponent } from 'utils/constants/banner';
import { MagritteNotificationName } from 'utils/constants/notification';

import createReducer, { ActionCreatorHelper } from 'store/createReducer';
import { CropInfo, ImageInfo, Images } from 'store/models/createBanner';

const INIT_IMAGES_STATE = {
    component: ImageComponent.Input,
};

export const INIT_IMAGES = {
    [BannerImageCropType.HH]: INIT_IMAGES_STATE,
    [BannerImageCropType.YandexAndVK]: INIT_IMAGES_STATE,
    [BannerImageCropType.VK]: INIT_IMAGES_STATE,
};

export type CroppedImages = Array<{
    originalImageId: number;
    cropType: BannerImageCropType;
    x: number;
    y: number;
    imageWidth: number;
    imageHeight: number;
}> | null;

export enum QuickCampaignStartType {
    Basic = 'BASIC',
    Standard = 'STANDARD',
    Maximum = 'MAXIMUM',
}

export enum QuickCampaignFieldName {
    VacancyId = 'vacancyId',
    Title = 'title',
    Text = 'text',
    QuickStartType = 'quickStartType',
    TotalBudget = 'budget',
    DailyBudget = 'budgetDaily',
    CroppedImages = 'croppedImages',
}

export enum QuickCampaignErrorName {
    RegionId = 'regionId',
}

export interface QuickCampaignConfigItem {
    type: QuickCampaignStartType;
    minBudget: number;
    minBudgetDaily: number;
}

export interface QuickCampaignFormData {
    [QuickCampaignFieldName.VacancyId]: string | null;
    [QuickCampaignFieldName.Title]: string;
    [QuickCampaignFieldName.Text]: string;
    [QuickCampaignFieldName.QuickStartType]: QuickCampaignStartType | null;
    [QuickCampaignFieldName.TotalBudget]: number | null;
    [QuickCampaignFieldName.DailyBudget]: number | null;
    [QuickCampaignFieldName.CroppedImages]: CroppedImages;
}

interface QuickCampaignFieldByName {
    fieldName: QuickCampaignFieldName;
    value: QuickCampaignStartType | string | number | boolean | null | CroppedImages;
}

export type QuickCampaignFormErrors = Partial<
    Record<QuickCampaignFieldName, string> & Record<QuickCampaignErrorName, MagritteNotificationName>
>;

// ----------------------- actions -------------------------------------
const SET_QUICK_CAMPAIGN_CONFIG = 'SET_QUICK_CAMPAIGN_CONFIG';
const SET_QUICK_CAMPAIGN_FIELD_BY_NAME = 'SET_QUICK_CAMPAIGN_FIELD_BY_NAME';
const SET_QUICK_CAMPAIGN_FORM_ERRORS = 'SET_QUICK_CAMPAIGN_FORM_ERRORS';
const RESET_QUICK_CAMPAIGN_FORM_DATA = 'RESET_QUICK_CAMPAIGN_FORM_DATA';
const RESET_QUICK_CAMPAIGN_BANNER_IMAGES = 'RESET_QUICK_CAMPAIGN_BANNER_IMAGES';
const SET_QUICK_CAMPAIGN_BANNER_IMAGE_BY_TYPE = 'SET_QUICK_CAMPAIGN_BANNER_IMAGE_BY_TYPE';
const SET_QUICK_CAMPAIGN_BANNER_IMAGE_CROP_INFO_BY_TYPE = 'SET_QUICK_CAMPAIGN_BANNER_IMAGE_CROP_INFO_BY_TYPE';
const SET_QUICK_CAMPAIGN_BANNER_IMAGE_IS_LOADED_IN_CROP_BY_TYPE =
    'SET_QUICK_CAMPAIGN_BANNER_IMAGE_IS_LOADED_IN_CROP_BY_TYPE';
const SET_QUICK_CAMPAIGN_PROF_ROLES = 'SET_QUICK_CAMPAIGN_PROF_ROLES';
const RESET_QUICK_CAMPAIGN_PROF_ROLES = 'RESET_QUICK_CAMPAIGN_PROF_ROLES';
const SET_QUICK_CAMPAIGN_IS_CROP_IMAGE_MODAL_VISIBLE = 'SET_QUICK_CAMPAIGN_IS_CROP_IMAGE_MODAL_VISIBLE';
const SET_QUICK_CAMPAIGN_IS_CROP_IMAGE_MODAL_WAS_OPENED_FROM_QUICK_GENERATION =
    'SET_QUICK_CAMPAIGN_IS_CROP_IMAGE_MODAL_WAS_OPENED_FROM_QUICK_GENERATION';
const RESET_QUICK_CAMPAIGN_CROPPED_IMAGES = 'RESET_QUICK_CAMPAIGN_CROPPED_IMAGES';
const SET_QUICK_CAMPAIGN_VACANCY_NAME = 'SET_QUICK_CAMPAIGN_VACANCY_NAME';

interface PayloadTypes {
    [SET_QUICK_CAMPAIGN_CONFIG]: QuickCampaignConfigItem[];
    [SET_QUICK_CAMPAIGN_FIELD_BY_NAME]: QuickCampaignFieldByName;
    [SET_QUICK_CAMPAIGN_FORM_ERRORS]: QuickCampaignFormErrors;
    [RESET_QUICK_CAMPAIGN_FORM_DATA]: null;
    [RESET_QUICK_CAMPAIGN_BANNER_IMAGES]: null;
    [SET_QUICK_CAMPAIGN_BANNER_IMAGE_BY_TYPE]: { cropType: BannerImageCropType; imageInfo: Partial<ImageInfo> };
    [SET_QUICK_CAMPAIGN_BANNER_IMAGE_CROP_INFO_BY_TYPE]: {
        cropType: BannerImageCropType;
        cropInfo: CropInfo;
    };
    [SET_QUICK_CAMPAIGN_BANNER_IMAGE_IS_LOADED_IN_CROP_BY_TYPE]: {
        cropType: BannerImageCropType;
        isLoadedInCrop: boolean;
    };
    [SET_QUICK_CAMPAIGN_PROF_ROLES]: string[];
    [RESET_QUICK_CAMPAIGN_PROF_ROLES]: null;
    [SET_QUICK_CAMPAIGN_IS_CROP_IMAGE_MODAL_VISIBLE]: boolean;
    [SET_QUICK_CAMPAIGN_IS_CROP_IMAGE_MODAL_WAS_OPENED_FROM_QUICK_GENERATION]: boolean;
    [RESET_QUICK_CAMPAIGN_CROPPED_IMAGES]: null;
    [SET_QUICK_CAMPAIGN_VACANCY_NAME]: string;
}

const ActionCreator = ActionCreatorHelper<PayloadTypes>();

export const setQuickCampaignConfig = ActionCreator(SET_QUICK_CAMPAIGN_CONFIG);
export const setQuickCampaignFieldByName = ActionCreator(SET_QUICK_CAMPAIGN_FIELD_BY_NAME);
export const setQuickCampaignFormErrors = ActionCreator(SET_QUICK_CAMPAIGN_FORM_ERRORS);
export const resetQuickCampaignFormData = ActionCreator(RESET_QUICK_CAMPAIGN_FORM_DATA);
export const resetQuickCampaignBannerImages = ActionCreator(RESET_QUICK_CAMPAIGN_BANNER_IMAGES);
export const setQuickCampaignBannerImageByType = ActionCreator(SET_QUICK_CAMPAIGN_BANNER_IMAGE_BY_TYPE);
export const setQuickCampaignBannerImageCropInfoByType = ActionCreator(
    SET_QUICK_CAMPAIGN_BANNER_IMAGE_CROP_INFO_BY_TYPE
);
export const setQuickCampaignBannerImageIsLoadedInCropByType = ActionCreator(
    SET_QUICK_CAMPAIGN_BANNER_IMAGE_IS_LOADED_IN_CROP_BY_TYPE
);
export const setQuickCampaignProfRoles = ActionCreator(SET_QUICK_CAMPAIGN_PROF_ROLES);
export const resetQuickCampaignProfRoles = ActionCreator(RESET_QUICK_CAMPAIGN_PROF_ROLES);
export const setQuickCampaignIsCropImageModalVisible = ActionCreator(SET_QUICK_CAMPAIGN_IS_CROP_IMAGE_MODAL_VISIBLE);
export const setQuickCampaignIsCropImageModalWasOpenedFromQuickGeneration = ActionCreator(
    SET_QUICK_CAMPAIGN_IS_CROP_IMAGE_MODAL_WAS_OPENED_FROM_QUICK_GENERATION
);
export const resetQuickCampaignCroppedImages = ActionCreator(RESET_QUICK_CAMPAIGN_CROPPED_IMAGES);
export const setQuickCampaignVacancyName = ActionCreator(SET_QUICK_CAMPAIGN_VACANCY_NAME);
// ----------------------- /actions -------------------------------------

// ----------------------- selectors ------------------------------------
const createFormDataFieldSelector =
    <K extends keyof QuickCampaignFormData>(fieldName: K) =>
    (state: DefaultRootState): QuickCampaignFormData[K] =>
        state.createQuickCampaign.formData[fieldName];

const createFormErrorsFieldSelector =
    <K extends keyof QuickCampaignFormErrors>(fieldName: K) =>
    (state: DefaultRootState): QuickCampaignFormErrors[K] =>
        state.createQuickCampaign.formErrors[fieldName];

export const selectQuickCampaignFormDataField = {
    [QuickCampaignFieldName.VacancyId]: createFormDataFieldSelector(QuickCampaignFieldName.VacancyId),
    [QuickCampaignFieldName.Title]: createFormDataFieldSelector(QuickCampaignFieldName.Title),
    [QuickCampaignFieldName.Text]: createFormDataFieldSelector(QuickCampaignFieldName.Text),
    [QuickCampaignFieldName.QuickStartType]: createFormDataFieldSelector(QuickCampaignFieldName.QuickStartType),
    [QuickCampaignFieldName.TotalBudget]: createFormDataFieldSelector(QuickCampaignFieldName.TotalBudget),
    [QuickCampaignFieldName.DailyBudget]: createFormDataFieldSelector(QuickCampaignFieldName.DailyBudget),
    [QuickCampaignFieldName.CroppedImages]: createFormDataFieldSelector(QuickCampaignFieldName.CroppedImages),
};

export const selectQuickCampaignFormErrors = {
    [QuickCampaignFieldName.VacancyId]: createFormErrorsFieldSelector(QuickCampaignFieldName.VacancyId),
    [QuickCampaignFieldName.Title]: createFormErrorsFieldSelector(QuickCampaignFieldName.Title),
    [QuickCampaignFieldName.Text]: createFormErrorsFieldSelector(QuickCampaignFieldName.Text),
    [QuickCampaignFieldName.QuickStartType]: createFormErrorsFieldSelector(QuickCampaignFieldName.QuickStartType),
    [QuickCampaignFieldName.TotalBudget]: createFormErrorsFieldSelector(QuickCampaignFieldName.TotalBudget),
    [QuickCampaignFieldName.DailyBudget]: createFormErrorsFieldSelector(QuickCampaignFieldName.DailyBudget),
    [QuickCampaignFieldName.CroppedImages]: createFormErrorsFieldSelector(QuickCampaignFieldName.CroppedImages),
    [QuickCampaignErrorName.RegionId]: createFormErrorsFieldSelector(QuickCampaignErrorName.RegionId),
};

export const selectQuickCampaignConfig = (state: DefaultRootState): QuickCampaignConfigItem[] =>
    state.createQuickCampaign.config;
export const selectQuickCampaignImages = (state: DefaultRootState): Images => state.createQuickCampaign.images;
export const selectQuickCampaignProfRoles = (state: DefaultRootState): string[] => state.createQuickCampaign.profRoles;
export const selectQuickCampaignVacancyName = (state: DefaultRootState): string =>
    state.createQuickCampaign.vacancyName;

// ----------------------- /selectors -----------------------------------

// ----------------------- reducer --------------------------------------
interface CreateQuickCampaignState {
    config: QuickCampaignConfigItem[];
    formData: QuickCampaignFormData;
    formErrors: QuickCampaignFormErrors;
    images: Images;
    profRoles: string[];
    isCropImageModalVisible: boolean;
    isCropImageModalWasOpenedFromQuickGeneration: boolean;
    vacancyName: string;
}

const CREATE_QUICK_CAMPAIGN_INITIAL_STATE: CreateQuickCampaignState = {
    config: [],
    formData: {
        [QuickCampaignFieldName.VacancyId]: null,
        [QuickCampaignFieldName.Title]: '',
        [QuickCampaignFieldName.Text]: '',
        [QuickCampaignFieldName.QuickStartType]: null,
        [QuickCampaignFieldName.TotalBudget]: null,
        [QuickCampaignFieldName.DailyBudget]: null,
        [QuickCampaignFieldName.CroppedImages]: null,
    },
    formErrors: {},
    images: INIT_IMAGES,
    profRoles: [],
    isCropImageModalVisible: false,
    isCropImageModalWasOpenedFromQuickGeneration: false,
    vacancyName: '',
};

export default createReducer<CreateQuickCampaignState, PayloadTypes>(CREATE_QUICK_CAMPAIGN_INITIAL_STATE, {
    [SET_QUICK_CAMPAIGN_CONFIG]: (state, { payload }) => ({
        ...state,
        config: payload,
    }),
    [SET_QUICK_CAMPAIGN_FIELD_BY_NAME]: (state, { payload }) => {
        const newState = {
            ...state,
            formData: {
                ...state.formData,
                [payload.fieldName]: payload.value,
            },
        };
        const formErrors = state.formErrors;
        const fieldName = payload.fieldName;
        if (formErrors && fieldName in formErrors) {
            const newFormErrors = { ...formErrors };
            delete newFormErrors[fieldName];
            newState.formErrors = newFormErrors;
        }
        return newState;
    },
    [SET_QUICK_CAMPAIGN_FORM_ERRORS]: (state, { payload }) => ({
        ...state,
        formErrors: payload,
    }),
    [RESET_QUICK_CAMPAIGN_FORM_DATA]: (state) => ({
        ...state,
        formData: { ...CREATE_QUICK_CAMPAIGN_INITIAL_STATE.formData },
    }),
    [SET_QUICK_CAMPAIGN_BANNER_IMAGE_BY_TYPE]: (state, { payload }) => ({
        ...state,
        images: {
            ...state.images,
            [payload.cropType]: payload.imageInfo,
        },
    }),
    [SET_QUICK_CAMPAIGN_BANNER_IMAGE_CROP_INFO_BY_TYPE]: (state, { payload }) => ({
        ...state,
        images: {
            ...state.images,
            [payload.cropType]: {
                ...state.images[payload.cropType],
                cropInfo: payload.cropInfo,
            },
        },
    }),
    [SET_QUICK_CAMPAIGN_BANNER_IMAGE_IS_LOADED_IN_CROP_BY_TYPE]: (state, { payload }) => ({
        ...state,
        images: {
            ...state.images,
            [payload.cropType]: {
                ...state.images[payload.cropType],
                isLoadedInCrop: payload.isLoadedInCrop,
            },
        },
    }),
    [RESET_QUICK_CAMPAIGN_BANNER_IMAGES]: (state) => ({
        ...state,
        images: INIT_IMAGES,
    }),
    [SET_QUICK_CAMPAIGN_PROF_ROLES]: (state, { payload }) => ({
        ...state,
        profRoles: payload,
    }),
    [RESET_QUICK_CAMPAIGN_PROF_ROLES]: (state) => ({
        ...state,
        profRoles: [],
    }),
    [SET_QUICK_CAMPAIGN_IS_CROP_IMAGE_MODAL_VISIBLE]: (state, { payload }) => ({
        ...state,
        isCropImageModalVisible: payload,
    }),
    [SET_QUICK_CAMPAIGN_IS_CROP_IMAGE_MODAL_WAS_OPENED_FROM_QUICK_GENERATION]: (state, { payload }) => ({
        ...state,
        isCropImageModalWasOpenedFromQuickGeneration: payload,
    }),
    [RESET_QUICK_CAMPAIGN_CROPPED_IMAGES]: (state) => ({
        ...state,
        formData: {
            ...state.formData,
            [QuickCampaignFieldName.CroppedImages]: null,
        },
    }),
    [SET_QUICK_CAMPAIGN_VACANCY_NAME]: (state, { payload }) => ({
        ...state,
        vacancyName: payload,
    }),
});
// ----------------------- /reducer --------------------------------------
